import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  TruckIcon, 
  ShieldCheckIcon, 
  ClockIcon, 
  GlobeAltIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const HERO_SLIDES = [
  'https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?auto=format&fit=crop&w=1920&q=80',
  'https://images.unsplash.com/photo-1578575437130-527eed3abbec?auto=format&fit=crop&w=1920&q=80',
  'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=1920&q=80',
  'https://images.unsplash.com/photo-1578853305419-c9b97a4bb073?auto=format&fit=crop&w=1920&q=80',
  'https://images.unsplash.com/photo-1755428855454-56c364d2a808?auto=format&fit=crop&w=1920&q=80',
];

const Home = () => {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();
  const { t } = useLanguage();

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % HERO_SLIDES.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const handleTrackSubmit = (e) => {
    e.preventDefault();
    if (trackingNumber.trim()) {
      navigate(`/track/${trackingNumber.trim()}`);
    }
  };

  const features = [
    {
      icon: TruckIcon,
      title: t.home.realTimeTracking,
      description: t.home.realTimeTrackingDesc
    },
    {
      icon: ShieldCheckIcon,
      title: t.home.secureDelivery,
      description: t.home.secureDeliveryDesc
    },
    {
      icon: ClockIcon,
      title: t.home.fastDelivery,
      description: t.home.fastDeliveryDesc
    },
    {
      icon: GlobeAltIcon,
      title: t.home.globalNetwork,
      description: t.home.globalNetworkDesc
    }
  ];

  const services = [
    {
      name: t.home.standardName,
      time: t.home.standardTime,
      price: t.home.standardPrice,
      features: [t.home.featTracking, t.home.featDeliveryConf, t.home.featBasicInsurance, t.home.featCustoms]
    },
    {
      name: t.home.expressName,
      time: t.home.expressTime,
      price: t.home.expressPrice,
      features: [t.home.featPriority, t.home.featRealTime, t.home.featSignature, t.home.featCustoms],
      popular: true
    },
    {
      name: t.home.premiumName,
      time: t.home.premiumTime,
      price: t.home.premiumPrice,
      features: [t.home.featGuaranteed, t.home.featPremiumTracking, t.home.featFullInsurance, t.home.featCustoms, t.home.featPriorityCustoms]
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section with Image Slider */}
      <section className="relative text-white overflow-hidden">
        <div className="absolute inset-0">
          {HERO_SLIDES.map((src, index) => (
            <img
              key={index}
              src={src}
              alt="Logistics and shipping"
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            />
          ))}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B1F3A]/95 via-[#123B63]/85 to-[#123B63]/60"></div>
        </div>
        {/* Slider dots */}
        <div className="absolute bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 z-20 flex space-x-2">
          {HERO_SLIDES.map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => setCurrentSlide(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentSlide ? 'w-8 bg-[#FF6B00]' : 'w-2 bg-white/50 hover:bg-white/80'
              }`}
              aria-label={`Slide ${index + 1}`}
            />
          ))}
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 md:py-28 lg:py-32 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center space-x-2 mb-5 sm:mb-6">
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full">
                <span className="text-xs sm:text-sm font-medium">{t.home.trustedBy}</span>
              </div>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-5 sm:mb-6 leading-tight">
              {t.home.heroTitle}
              <span className="block text-[#FF6B00]">{t.home.heroTitleAccent}</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-300 mb-7 sm:mb-8 max-w-2xl leading-relaxed">
              {t.home.heroDesc}
            </p>

            {/* Quick Track Form */}
            <div className="max-w-md">
              <form onSubmit={handleTrackSubmit} className="flex flex-col sm:flex-row shadow-2xl rounded-lg overflow-hidden gap-2 sm:gap-0 sm:rounded-lg">
                <input
                  type="text"
                  placeholder={t.home.trackPlaceholder}
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  className="flex-1 px-4 py-3 sm:py-3.5 text-gray-900 focus:outline-none focus:ring-0 rounded-lg sm:rounded-none text-sm sm:text-base"
                />
                <button
                  type="submit"
                  className="px-6 py-3 sm:py-3.5 bg-[#FF6B00] hover:bg-[#e65f00] transition-all duration-200 flex items-center justify-center rounded-lg sm:rounded-none"
                >
                  <MagnifyingGlassIcon className="h-5 w-5" />
                  <span className="ml-2 font-semibold">{t.home.track}</span>
                </button>
              </form>
              <p className="text-gray-300 text-sm mt-2">
                {t.home.orVisit} <Link to="/track" className="underline hover:text-[#FF6B00]">{t.home.visitTracking}</Link>
              </p>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-2 sm:gap-3 md:gap-4 mt-8 sm:mt-10">
              <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm border border-white/10 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg">
                <ShieldCheckIcon className="h-4 w-4 sm:h-5 sm:w-5 text-[#FF6B00]" />
                <span className="text-xs sm:text-sm">{t.home.ssl}</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm border border-white/10 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg">
                <GlobeAltIcon className="h-4 w-4 sm:h-5 sm:w-5 text-[#FF6B00]" />
                <span className="text-xs sm:text-sm">{t.home.countries}</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm border border-white/10 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg">
                <CheckCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-[#FF6B00]" />
                <span className="text-xs sm:text-sm">{t.home.onTime}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Freight Modes Showcase */}
      <section className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-12">
            <div className="inline-block bg-orange-100 text-[#FF6B00] px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4">
              {t.home.whatWeMove}
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#0B1F3A] mb-3 sm:mb-4">
              {t.home.freightSolutions}
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-[#64748B] max-w-2xl mx-auto px-2">
              {t.home.freightDesc}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            <div className="group relative rounded-2xl overflow-hidden shadow-lg h-64 sm:h-72 md:h-80">
              <img
                src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&w=900&q=80"
                alt="Cargo airplane in the sky"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1F3A]/90 via-[#0B1F3A]/30 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
                <h3 className="text-white text-lg sm:text-xl font-bold mb-1">{t.home.airFreight}</h3>
                <p className="text-gray-300 text-xs sm:text-sm">{t.home.airFreightDesc}</p>
              </div>
            </div>
            <div className="group relative rounded-2xl overflow-hidden shadow-lg h-64 sm:h-72 md:h-80">
              <img
                src="https://images.unsplash.com/photo-1578575437130-527eed3abbec?auto=format&fit=crop&w=900&q=80"
                alt="Container ship at port"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1F3A]/90 via-[#0B1F3A]/30 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
                <h3 className="text-white text-lg sm:text-xl font-bold mb-1">{t.home.seaFreight}</h3>
                <p className="text-gray-300 text-xs sm:text-sm">{t.home.seaFreightDesc}</p>
              </div>
            </div>
            <div className="group relative rounded-2xl overflow-hidden shadow-lg h-64 sm:h-72 md:h-80">
              <img
                src="https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&w=900&q=80"
                alt="Courier delivering packages"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1F3A]/90 via-[#0B1F3A]/30 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
                <h3 className="text-white text-lg sm:text-xl font-bold mb-1">{t.home.doorToDoor}</h3>
                <p className="text-gray-300 text-xs sm:text-sm">{t.home.doorToDoorDesc}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <div className="inline-block bg-orange-100 text-[#FF6B00] px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4">
              {t.home.ourAdvantages}
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#0B1F3A] mb-3 sm:mb-4">
              {t.home.whyChoose}
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-[#64748B] max-w-2xl mx-auto px-2">
              {t.home.whyChooseDesc}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center group p-4 sm:p-6 rounded-2xl hover:shadow-xl transition-all duration-300 border border-[#E2E8F0] hover:border-orange-200">
                <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-[#0B1F3A] rounded-full mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <feature.icon className="h-7 w-7 sm:h-8 sm:w-8 text-[#FF6B00]" />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-[#0B1F3A] mb-2">
                  {feature.title}
                </h3>
                <p className="text-[#64748B]">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 sm:py-20 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <div className="inline-block bg-orange-100 text-[#FF6B00] px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4">
              {t.home.pricingPlans}
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#0B1F3A] mb-3 sm:mb-4">
              {t.home.ourServices}
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-[#64748B]">
              {t.home.chooseService}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
            {services.map((service, index) => (
              <div 
                key={index} 
                className={`card relative p-6 sm:p-8 rounded-2xl transition-all duration-300 hover:shadow-2xl ${
                  service.popular ? 'ring-2 ring-[#FF6B00] bg-orange-50' : 'bg-white'
                }`}
              >
                {service.popular && (
                  <div className="absolute -top-3 sm:-top-4 left-1/2 transform -translate-x-1/2 z-10">
                    <span className="bg-[#FF6B00] text-white px-3 sm:px-4 py-1 rounded-full text-xs sm:text-sm font-semibold shadow-lg whitespace-nowrap">
                      {t.home.mostPopular}
                    </span>
                  </div>
                )}
                
                <div className="text-center">
                  <h3 className="text-lg sm:text-xl font-bold text-[#0B1F3A] mb-2">
                    {service.name}
                  </h3>
                  <p className="text-2xl sm:text-3xl font-bold text-[#FF6B00] mb-1">
                    {service.price}
                  </p>
                  <p className="text-[#64748B] mb-6">
                    {service.time}
                  </p>
                  
                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center">
                        <CheckCircleIcon className="h-5 w-5 text-[#16A34A] mr-2 flex-shrink-0" />
                        <span className="text-[#17202A]">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Link
                    to="/contact"
                    className={`btn w-full ${
                      service.popular 
                        ? 'bg-[#FF6B00] hover:bg-[#e65f00] text-white' 
                        : 'btn-outline'
                    }`}
                  >
                    {t.home.getStarted}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="relative py-16 sm:py-20 text-white overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1519003722824-194d4455a60c?auto=format&fit=crop&w=1920&q=80"
            alt="Shipping containers at port"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#0B1F3A]/90"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8 text-center">
            <div className="p-3 sm:p-6">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#FF6B00] mb-2">
                10M+
              </div>
              <div className="text-xs sm:text-sm text-gray-300 font-medium">{t.home.packagesDelivered}</div>
            </div>
            <div className="p-3 sm:p-6">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#FF6B00] mb-2">
                200+
              </div>
              <div className="text-xs sm:text-sm text-gray-300 font-medium">{t.home.countriesServed}</div>
            </div>
            <div className="p-3 sm:p-6">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#FF6B00] mb-2">
                99.8%
              </div>
              <div className="text-xs sm:text-sm text-gray-300 font-medium">{t.home.onTimeDelivery}</div>
            </div>
            <div className="p-3 sm:p-6">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#FF6B00] mb-2">
                24/7
              </div>
              <div className="text-xs sm:text-sm text-gray-300 font-medium">{t.home.customerSupport}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <div className="inline-block bg-orange-100 text-[#FF6B00] px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4">
              {t.home.customerReviews}
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#0B1F3A] mb-3 sm:mb-4">
              {t.home.whatCustomersSay}
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-[#64748B] max-w-2xl mx-auto px-2">
              {t.home.joinThousands}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            <div className="card p-5 sm:p-6 rounded-2xl hover:shadow-xl transition-all duration-300">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className="text-yellow-400 text-xl">★</span>
                ))}
              </div>
              <p className="text-[#17202A] mb-4">
                {t.home.review1}
              </p>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-[#0B1F3A] rounded-full flex items-center justify-center text-white font-bold">
                  RH
                </div>
                <div className="ml-3">
                  <p className="font-semibold text-[#0B1F3A]">{t.home.review1Name}</p>
                  <p className="text-sm text-[#64748B]">{t.home.review1Role}</p>
                </div>
              </div>
            </div>

            <div className="card p-6 rounded-2xl hover:shadow-xl transition-all duration-300">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className="text-yellow-400 text-xl">★</span>
                ))}
              </div>
              <p className="text-[#17202A] mb-4">
                {t.home.review2}
              </p>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-[#123B63] rounded-full flex items-center justify-center text-white font-bold">
                  KS
                </div>
                <div className="ml-3">
                  <p className="font-semibold text-[#0B1F3A]">{t.home.review2Name}</p>
                  <p className="text-sm text-[#64748B]">{t.home.review2Role}</p>
                </div>
              </div>
            </div>

            <div className="card p-6 rounded-2xl hover:shadow-xl transition-all duration-300">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className="text-yellow-400 text-xl">★</span>
                ))}
              </div>
              <p className="text-[#17202A] mb-4">
                {t.home.review3}
              </p>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-[#0B1F3A] rounded-full flex items-center justify-center text-white font-bold">
                  TW
                </div>
                <div className="ml-3">
                  <p className="font-semibold text-[#0B1F3A]">{t.home.review3Name}</p>
                  <p className="text-sm text-[#64748B]">{t.home.review3Role}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-16 sm:py-20 text-white overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1553413077-190dd305871c?auto=format&fit=crop&w=1920&q=80"
            alt="Modern logistics warehouse"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B1F3A]/95 to-[#123B63]/80"></div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">
            {t.home.readyToShip}
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-300 mb-6 sm:mb-8">
            {t.home.joinThousands}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Link to="/contact" className="bg-[#FF6B00] text-white hover:bg-[#e65f00] px-6 sm:px-8 py-3.5 sm:py-4 rounded-lg font-semibold text-base sm:text-lg transition-all duration-200 shadow-lg hover:shadow-xl text-center">
              {t.home.getQuote}
            </Link>
            <Link to="/track" className="border-2 border-white text-white hover:bg-white hover:text-[#0B1F3A] px-6 sm:px-8 py-3.5 sm:py-4 rounded-lg font-semibold text-base sm:text-lg transition-all duration-200 text-center">
              {t.home.trackPackage}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
